import { useEffect, useRef } from "react";

interface UseCockpitExpelledPlayerEffectParams {
  selectedPlayer: { id: string } | null;
  expelledPlayerIds: Set<string>;
  resetFlow: () => void;
  setToast: (toast: { message: string } | null) => void;
  t: (key: string, fallback: string) => string;
}

export const useCockpitExpelledPlayerEffect = ({
  selectedPlayer,
  expelledPlayerIds,
  resetFlow,
  setToast,
  t,
}: UseCockpitExpelledPlayerEffectParams): void => {
  // Derive a primitive trigger. `expelledPlayerIds` is a fresh Set on every
  // render, so depending on it directly re-runs this effect each render; paired
  // with the setToast/resetFlow calls below (which re-render without clearing
  // the selected player) that produced an infinite update loop. We instead key
  // off the selected player's id + its expulsion state, and a ref guard so the
  // toast fires once per distinct expulsion.
  const selectedId = selectedPlayer?.id ?? null;
  const isSelectedExpelled =
    selectedId !== null && expelledPlayerIds.has(selectedId);
  const handledExpulsionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSelectedExpelled || selectedId === null) {
      handledExpulsionRef.current = null;
      return;
    }
    if (handledExpulsionRef.current === selectedId) return;
    handledExpulsionRef.current = selectedId;

    resetFlow();
    setToast({
      message: t(
        "playerExpelled",
        "Player is expelled and cannot log actions.",
      ),
    });
    const timeout = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timeout);
  }, [isSelectedExpelled, selectedId, resetFlow, setToast, t]);
};
